import { useState, useEffect } from 'react';

export enum OperationType {
  CREATE = 'create',
  UPDATE = 'update',
  DELETE = 'delete',
  LIST = 'list',
  GET = 'get',
  WRITE = 'write',
}

interface FirestoreErrorInfo {
  error: string;
  operationType: OperationType;
  path: string | null;
  authInfo: {
    userId?: string | null;
    email?: string | null;
    emailVerified?: boolean | null;
    isAnonymous?: boolean | null;
    tenantId?: string | null;
    providerInfo?: {
      providerId?: string | null;
      email?: string | null;
    }[];
  };
}

function handleFirestoreError(error: unknown, operationType: OperationType, path: string | null) {
  const errInfo = {
    error: error instanceof Error ? error.message : String(error),
    operationType,
    path
  };
  console.error('Firestore Error: ', JSON.stringify(errInfo));
  throw new Error(JSON.stringify(errInfo));
}

export interface UserPreferences {
  struggle: string;
  timeOfDay: string;
  meditates: boolean;
  audioDevice: string;
}

export interface UserState {
  id: string;
  email: string;
  subscriptionStatus?: string;
  geminiApiKey?: string;
  companyId?: string;
  role?: string;
}

export interface DailyScores {
  date: string; // YYYY-MM-DD
  sleep: number;
  stress: number;
  focus: number;
}

export interface SessionRecord {
  id: string;
  date: string;
  durationMinutes: number;
  goal: string;
  mode?: string;
  noise?: string;
  timeOfDay?: string;
  moodBefore?: number;
  moodAfter?: number;
  focusRating?: number;
  feelings?: string;
}

export interface CheckInRecord {
  date: string; // YYYY-MM-DD
  type: 'morning' | 'evening';
  value: string;
}

export interface Task {
  id: string;
  content: string;
  completed: boolean;
}

export interface BrainwavePreset {
  id: string;
  name: string;
  baseFreq: number;
  beatFreq: number;
  volume: number;
  noiseType: 'none' | 'white' | 'pink' | 'brown';
  noiseVolume: number;
  binauralSpatialEnabled: boolean;
  binauralPos: { x: number; y: number; z: number };
  noiseSpatialEnabled: boolean;
  noisePos: { x: number; y: number; z: number };
  dualHemisphere: boolean;
  leftIntensity: number;
  rightIntensity: number;
}

export interface BrainwaveSettings {
  dualHemisphere: boolean;
  leftIntensity: number;
  rightIntensity: number;
}

export interface AmbianceSettings {
  theme: 'circadian' | 'charcoal' | 'amber' | 'forest' | 'ocean' | 'grayscale';
  backgroundType: 'solid' | 'gradient' | 'particles' | 'waves' | 'custom';
  lightIntensity: 'dim' | 'medium' | 'bright';
  motion: 'still' | 'light' | 'immersive' | 'static' | 'reduced' | 'minimal';
  colorTemperature: number;
  autoNightMode: boolean;
  customBackgroundImage: string | null;
  seizureSafeMode: boolean;
  volumeLimiter: boolean;
  tinnitusReliefMode: boolean;
  antiStartleMode: boolean;
  vestibularSafeMode: boolean;
  sensorySmoothingMode: boolean;
  lowStimulationUI: boolean;
}

const defaultAmbiance: AmbianceSettings = {
  theme: 'charcoal',
  backgroundType: 'gradient',
  lightIntensity: 'medium',
  motion: 'light',
  colorTemperature: 50,
  autoNightMode: true,
  customBackgroundImage: null,
  seizureSafeMode: false,
  volumeLimiter: true,
  tinnitusReliefMode: false,
  antiStartleMode: false,
  vestibularSafeMode: false,
  sensorySmoothingMode: false,
  lowStimulationUI: false
};

const defaultBrainwaveSettings: BrainwaveSettings = {
  dualHemisphere: false,
  leftIntensity: 100,
  rightIntensity: 100
};

export function useUserStore() {
  const [preferences, setPreferences] = useState<UserPreferences | null>(() => {
    const saved = localStorage.getItem('neurotone_prefs');
    return saved ? JSON.parse(saved) : null;
  });

  const [dailyScores, setDailyScores] = useState<DailyScores[]>(() => {
    const saved = localStorage.getItem('neurotone_scores');
    return saved ? JSON.parse(saved) : [];
  });

  const [sessions, setSessions] = useState<SessionRecord[]>(() => {
    const saved = localStorage.getItem('neurotone_sessions');
    return saved ? JSON.parse(saved) : [];
  });

  const [checkIns, setCheckIns] = useState<CheckInRecord[]>(() => {
    const saved = localStorage.getItem('neurotone_checkins');
    return saved ? JSON.parse(saved) : [];
  });

  const [ambiance, setAmbiance] = useState<AmbianceSettings>(() => {
    const saved = localStorage.getItem('neurotone_ambiance');
    return saved ? JSON.parse(saved) : defaultAmbiance;
  });

  const [brainwaveSettings, setBrainwaveSettings] = useState<BrainwaveSettings>(() => {
    const saved = localStorage.getItem('neurotone_brainwave');
    return saved ? JSON.parse(saved) : defaultBrainwaveSettings;
  });

  const [brainwavePresets, setBrainwavePresets] = useState<BrainwavePreset[]>(() => {
    const saved = localStorage.getItem('neurotone_brainwave_presets');
    return saved ? JSON.parse(saved) : [];
  });

  const [tasks, setTasks] = useState<Task[]>(() => {
    const saved = localStorage.getItem('neurotone_tasks');
    return saved ? JSON.parse(saved) : [
      { id: '1', content: 'Define primary objective for deep work', completed: false },
      { id: '2', content: 'Set phone to Do Not Disturb', completed: false }
    ];
  });

  const [remindersEnabled, setRemindersEnabled] = useState<boolean>(() => {
    const saved = localStorage.getItem('neurotone_reminders');
    return saved ? JSON.parse(saved) : false;
  });

  const [isPremium, setIsPremium] = useState<boolean>(() => {
    const saved = localStorage.getItem('neurotone_premium');
    return saved ? JSON.parse(saved) : false;
  });

  useEffect(() => {
    if (preferences) localStorage.setItem('neurotone_prefs', JSON.stringify(preferences));
  }, [preferences]);

  useEffect(() => {
    localStorage.setItem('neurotone_scores', JSON.stringify(dailyScores));
  }, [dailyScores]);

  useEffect(() => {
    localStorage.setItem('neurotone_sessions', JSON.stringify(sessions));
  }, [sessions]);

  useEffect(() => {
    localStorage.setItem('neurotone_checkins', JSON.stringify(checkIns));
  }, [checkIns]);

  useEffect(() => {
    localStorage.setItem('neurotone_ambiance', JSON.stringify(ambiance));
  }, [ambiance]);

  useEffect(() => {
    localStorage.setItem('neurotone_brainwave', JSON.stringify(brainwaveSettings));
  }, [brainwaveSettings]);

  useEffect(() => {
    localStorage.setItem('neurotone_brainwave_presets', JSON.stringify(brainwavePresets));
  }, [brainwavePresets]);

  useEffect(() => {
    localStorage.setItem('neurotone_premium', JSON.stringify(isPremium));
  }, [isPremium]);

  useEffect(() => {
    localStorage.setItem('neurotone_tasks', JSON.stringify(tasks));
  }, [tasks]);

  useEffect(() => {
    localStorage.setItem('neurotone_reminders', JSON.stringify(remindersEnabled));
  }, [remindersEnabled]);

  // Real-time Firestore Sync Effect (Lazy Loaded)
  useEffect(() => {
    // Skip initiating auth listener for anonymous landing page visitors
    const hasAuthToken = typeof window !== 'undefined' && (
      localStorage.getItem('neurotone_user') !== null ||
      Object.keys(localStorage).some(k => k.startsWith('firebase:authUser'))
    );
    if (!hasAuthToken) return;

    let isUnmounted = false;
    let unsubscribeAuth: (() => void) | null = null;
    let unsubscribeSessions: (() => void) | null = null;
    let unsubscribePresets: (() => void) | null = null;

    (async () => {
      try {
        const { auth, db } = await import('../firebase');
        const { onAuthStateChanged } = await import('firebase/auth');
        const { collection, onSnapshot, doc, setDoc, getDocFromServer } = await import('firebase/firestore');

        if (isUnmounted) return;

        unsubscribeAuth = onAuthStateChanged(auth, async (fbUser) => {
          if (fbUser) {
            const uid = fbUser.uid;
            try {
              await getDocFromServer(doc(db, 'test', 'connection'));
            } catch (error) {
              if (error instanceof Error && error.message.includes('the client is offline')) {
                console.error("Please check your Firebase configuration.");
              }
            }

            // Real-time Sessions Listener
            const sessionsRef = collection(db, 'users', uid, 'sessions');
            unsubscribeSessions = onSnapshot(sessionsRef, (snapshot) => {
              const remoteSessions: SessionRecord[] = [];
              snapshot.forEach((docSnap) => {
                remoteSessions.push(docSnap.data() as SessionRecord);
              });
              setSessions((prev) => {
                const mergedMap = new Map<string, SessionRecord>();
                remoteSessions.forEach((s) => mergedMap.set(s.id, s));
                prev.forEach((s) => {
                  if (!mergedMap.has(s.id)) {
                    mergedMap.set(s.id, s);
                    try {
                      setDoc(doc(db, 'users', uid, 'sessions', s.id), s)
                        .catch((err) => handleFirestoreError(err, OperationType.WRITE, `users/${uid}/sessions/${s.id}`));
                    } catch (err) {
                      handleFirestoreError(err, OperationType.WRITE, `users/${uid}/sessions/${s.id}`);
                    }
                  }
                });
                return Array.from(mergedMap.values()).sort((a, b) => new Date(a.date).getTime() - new Date(b.date).getTime());
              });
            }, (error) => {
              handleFirestoreError(error, OperationType.GET, `users/${uid}/sessions`);
            });

            // Real-time Presets Listener
            const presetsRef = collection(db, 'users', uid, 'presets');
            unsubscribePresets = onSnapshot(presetsRef, (snapshot) => {
              const remotePresets: BrainwavePreset[] = [];
              snapshot.forEach((docSnap) => {
                remotePresets.push(docSnap.data() as BrainwavePreset);
              });
              setBrainwavePresets((prev) => {
                const mergedMap = new Map<string, BrainwavePreset>();
                remotePresets.forEach((p) => mergedMap.set(p.id, p));
                prev.forEach((p) => {
                  if (!mergedMap.has(p.id)) {
                    mergedMap.set(p.id, p);
                    try {
                      setDoc(doc(db, 'users', uid, 'presets', p.id), p)
                        .catch((err) => handleFirestoreError(err, OperationType.WRITE, `users/${uid}/presets/${p.id}`));
                    } catch (err) {
                      handleFirestoreError(err, OperationType.WRITE, `users/${uid}/presets/${p.id}`);
                    }
                  }
                });
                return Array.from(mergedMap.values());
              });
            }, (error) => {
              handleFirestoreError(error, OperationType.GET, `users/${uid}/presets`);
            });

          } else {
            if (unsubscribeSessions) { unsubscribeSessions(); unsubscribeSessions = null; }
            if (unsubscribePresets) { unsubscribePresets(); unsubscribePresets = null; }
          }
        });
      } catch (err) {
        // Fallback for offline mode
      }
    })();

    return () => {
      isUnmounted = true;
      if (unsubscribeAuth) unsubscribeAuth();
      if (unsubscribeSessions) unsubscribeSessions();
      if (unsubscribePresets) unsubscribePresets();
    };
  }, []);

  const savePreferences = (prefs: UserPreferences) => setPreferences(prefs);
  
  const addDailyScore = (score: DailyScores) => {
    setDailyScores(prev => {
      const existing = prev.findIndex(s => s.date === score.date);
      if (existing >= 0) {
        const newScores = [...prev];
        newScores[existing] = score;
        return newScores;
      }
      return [...prev, score];
    });
  };

  const addSession = (session: SessionRecord) => {
    setSessions(prev => [...prev, session]);
    if (auth.currentUser) {
      const uid = auth.currentUser.uid;
      try {
        setDoc(doc(db, 'users', uid, 'sessions', session.id), session)
          .catch((err) => handleFirestoreError(err, OperationType.WRITE, `users/${uid}/sessions/${session.id}`));
      } catch (err) {
        handleFirestoreError(err, OperationType.WRITE, `users/${uid}/sessions/${session.id}`);
      }
    }
  };

  const addCheckIn = (checkIn: CheckInRecord) => {
    setCheckIns(prev => {
      // Remove existing check-in of the same type for the same date
      const filtered = prev.filter(c => !(c.date === checkIn.date && c.type === checkIn.type));
      return [...filtered, checkIn];
    });
  };

  const updateAmbiance = (settings: Partial<AmbianceSettings>) => {
    setAmbiance(prev => ({ ...prev, ...settings }));
  };

  const updateBrainwaveSettings = (settings: Partial<BrainwaveSettings>) => {
    setBrainwaveSettings(prev => ({ ...prev, ...settings }));
  };

  const addBrainwavePreset = (preset: BrainwavePreset) => {
    setBrainwavePresets(prev => [...prev, preset]);
    if (auth.currentUser) {
      const uid = auth.currentUser.uid;
      try {
        setDoc(doc(db, 'users', uid, 'presets', preset.id), preset)
          .catch((err) => handleFirestoreError(err, OperationType.WRITE, `users/${uid}/presets/${preset.id}`));
      } catch (err) {
        handleFirestoreError(err, OperationType.WRITE, `users/${uid}/presets/${preset.id}`);
      }
    }
  };

  const removeBrainwavePreset = (id: string) => {
    setBrainwavePresets(prev => prev.filter(p => p.id !== id));
    if (auth.currentUser) {
      const uid = auth.currentUser.uid;
      try {
        deleteDoc(doc(db, 'users', uid, 'presets', id))
          .catch((err) => handleFirestoreError(err, OperationType.DELETE, `users/${uid}/presets/${id}`));
      } catch (err) {
        handleFirestoreError(err, OperationType.DELETE, `users/${uid}/presets/${id}`);
      }
    }
  };

  const unlockPremium = () => setIsPremium(true);

  return {
    preferences,
    savePreferences,
    dailyScores,
    addDailyScore,
    sessions,
    addSession,
    checkIns,
    addCheckIn,
    ambiance,
    updateAmbiance,
    brainwaveSettings,
    updateBrainwaveSettings,
    brainwavePresets,
    addBrainwavePreset,
    removeBrainwavePreset,
    isPremium,
    unlockPremium,
    tasks,
    setTasks,
    remindersEnabled,
    setRemindersEnabled
  };
}
